import axios from 'axios';
import store from '@/store';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import AuthClient from '@/utils/auth';

const MAX_RETRY = 2;
const RETRY_NUMBER = 5;
const API_BASE = {
  baseURL: process.env.API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
};

const LOGOUT_FLAG_KEY = 'loggingOutAt';
const LOGOUT_FLAG_TTL_MS = 60000;

const isLoggingOutNow = () => {
  try {
    const raw = window && window.sessionStorage && window.sessionStorage.getItem(LOGOUT_FLAG_KEY);
    if (!raw) {
      return false;
    }
    const at = Number(raw);
    if (!Number.isFinite(at)) {
      window.sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      return false;
    }
    const tooOld = Date.now() - at > LOGOUT_FLAG_TTL_MS;
    if (tooOld) {
      window.sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

const requestInterceptors = (request) => {
  // NOTE: When sending FormData (file uploads), the browser/axios must set
  // `Content-Type: multipart/form-data; boundary=...` automatically.
  // If we keep a global JSON Content-Type, servers may reject the request.
  try {
    const isFormData = typeof FormData !== 'undefined' && request && request.data instanceof FormData;
    if (isFormData && request.headers) {
      if (typeof request.headers.delete === 'function') {
        request.headers.delete('Content-Type');
      } else {
        delete request.headers['Content-Type'];
        delete request.headers['content-type'];
      }
    }
  } catch (e) {
    console.error('request interceptors error', e);
  }

  return AuthClient.getAccessToken()
    .then((accessToken) => {
      request.headers.Authorization = 'JWT ' + accessToken;
      return request;
    })
    .catch((error) => {
      if (isLoggingOutNow()) {
        return Promise.reject(new Error('logging_out'));
      }

      const errorCode = error && (error.error || error.code);
      const errorMessage = error && (error.message || (typeof error === 'string' ? error : ''));
      const isLoginRequired =
        errorCode === 'login_required' ||
        errorCode === 'consent_required' ||
        (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('login required'));

      if (isLoginRequired) {
        if (isLoggingOutNow()) {
          return Promise.reject(new Error('logging_out'));
        }

        store.dispatch('message/setWarningMessage', {
          title: 'セッションタイムアウト',
          message: 'セッションがタイムアウトしました。再度ログインしてください。',
          isLoginError: true
        });

        const currentPath = window.location.pathname || '';
        const isAuthRoute = currentPath.startsWith('/login') || currentPath.startsWith('/callback');
        if (!isAuthRoute) {
          const redirect = currentPath + (window.location.search || '') + (window.location.hash || '');
          window.location.assign(`/login?redirect=${encodeURIComponent(redirect)}`);
        }

        // リダイレクト開始後も axios チェーンを終了させる（呼び出し元で握り潰せるよう明示的に reject）
        return Promise.reject(new Error('login_redirect'));
      }

      console.error('token error', error);
      return Promise.reject(error);
    });
};

const rejectRequestInterceptors = (error) => {
  return Promise.reject(error);
};

const responseInterceptors = (response) => {
  return response;
};

const rejectResponseInterceptors = (error) => {
  if (error && error.message === 'login_redirect') {
    return Promise.reject(error);
  }

  // 明示的なログアウト中は、in-flight の API が失敗しても共通メッセージを出さない。
  // （401以外の通信エラーやキャンセルでも「想定外」表示になるため）
  if (isLoggingOutNow()) {
    return Promise.reject(error);
  }

  // `/callback` では最終的に Login へ遷移することが多く、
  // ここで共通エラーダイアログ（赤い画面）が一瞬表示されるのを防ぐ。
  // また `verify_user/` は AuthCallback 側でメッセージ出し分けを行う。
  try {
    const currentPath = window.location.pathname || '';
    const requestUrl = (error && error.config && error.config.url) || '';
    const isAuthCallbackRoute = typeof currentPath === 'string' && currentPath.startsWith('/callback');
    const isVerifyUserRequest = typeof requestUrl === 'string' && requestUrl.includes('verify_user/');
    if (isAuthCallbackRoute || isVerifyUserRequest) {
      return Promise.reject(error);
    }
  } catch (e) {
    // ignore
  }

  // TODO: クラウドサインの場合を考慮
  const status = error.response ? error.response.status : 500;

  const extractApiErrorMessage = (err) => {
    const data = err && err.response && err.response.data;
    if (!data) {
      return null;
    }
    if (typeof data === 'string') {
      return data;
    }
    if (typeof data === 'object') {
      if (typeof data.error === 'string' && data.error) return data.error;
      if (typeof data.detail === 'string' && data.detail) return data.detail;
      if (typeof data.message === 'string' && data.message) return data.message;
    }
    return null;
  };

  const apiErrorMessage = extractApiErrorMessage(error);
  // const isCloudsignReview = localStorage.getItem('isCloudsignReview') === 'true';
  // const loginName = isCloudsignReview ? 'LoginForCloudsignReview' : 'Login';
  if (status === 401) {
    store.dispatch('message/setWarningMessage', {
      title: 'セッションタイムアウト',
      message: 'セッションがタイムアウトしました。再度ログインしてください。',
      isLoginError: true
    });
  } else if (status === 403) {
    store.dispatch('message/setErrorMessage', {
      message: apiErrorMessage || (error.response && error.response.data && error.response.data.error) || '権限がありません。',
      isLoginError: true
    });
  } else if (status === 400) {
    store.dispatch('message/setErrorMessage', {
      message: apiErrorMessage || '入力内容に誤りがあります。'
    });
  } else {
    store.dispatch('message/setErrorMessage', {
      message: apiErrorMessage || '想定外のエラーです。'
    });
  }
  return Promise.reject(error);
};

// apiWithoutAuthentication
const apiWithoutAuthentication = axios.create(API_BASE);
apiWithoutAuthentication.interceptors.response.use(responseInterceptors, rejectResponseInterceptors);

// default api
const api = axios.create(API_BASE);
axiosRetry(api, {
  retries: MAX_RETRY,
  retryDelay: () => exponentialDelay(RETRY_NUMBER)
});
api.install = function (Vue) {
  Object.defineProperty(Vue.prototype, '$request', {
    get() {
      return api;
    }
  });
};
api.interceptors.request.use(requestInterceptors, rejectRequestInterceptors);
api.interceptors.response.use(responseInterceptors, rejectResponseInterceptors);

// apiWithoutResponseInterceptors
const apiWithoutResponseInterceptors = axios.create(API_BASE);
apiWithoutResponseInterceptors.interceptors.request.use(requestInterceptors, rejectRequestInterceptors);

const apiWithoutInterceptors = axios.create(API_BASE);

export default api;
export { apiWithoutAuthentication, apiWithoutResponseInterceptors, apiWithoutInterceptors };
